/**
 * Frontend mirror of backend types. We don't share these via a workspace
 * to keep build pipelines independent; copy is small + low-churn.
 */

export type UserRole = 'system' | 'admin' | 'member';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  github_handle: string | null;
  role: UserRole;
  active: boolean;
  created_at: string;
}

export type TaskStatus =
  | 'queued'
  | 'planning'
  | 'pending_approval'
  | 'pending_questions'
  | 'pending_questions_in_execution'
  | 'running'
  | 'held'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Worker-pool routing — see panel backend types for the full contract.
 * Used by the UI to show planning-vs-execution badges + ordering.
 */
export type TaskPhase = 'planning' | 'approved' | 'executing' | 'done';

export interface ClarifyingQuestion {
  id: string;
  question: string;
  why?: string;
  options: Array<{ value: string; hint?: string; recommended?: boolean }>;
}

export interface ClarifyingAnswer {
  question_id: string;
  answer: string;
  custom?: boolean;
}

export interface Project {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  local_path: string;
  repo_url: string | null;
  default_branch: string;
  status: 'active' | 'archived';
  /** Branch each planning worktree is pinned to (default 'main'). */
  planning_base_branch: string;
  /** How many planning workers can run concurrently for this project (1..10). */
  planning_concurrency: number;
  /** Which pipeline runs this project's tasks: 'v1' (default) or 'v2'. */
  pipeline_version: string;
  /** v2 execution mode: 'standard' (default) or 'phase-agentic'. */
  execution_mode: string;
  /** v2 git behavior: 'per_step' or 'squash' (default). */
  commit_strategy: string;
  /** v2: push the feature branch on success (opt-in). */
  auto_push: boolean;
  /** v2: remote to push to (default 'origin'). */
  push_remote: string;
  /** v2 capability: project can build/test (run the verifier). Default true. */
  full_setup: boolean;
  /** v2 capability: project can run a browser smoke check. Default false. */
  browser_testing: boolean;
  created_at: string;
}

/**
 * Mirror of the pipeline's RiskAssessment shape — decoupled so the
 * Panel doesn't import from the Pipeline package at build time.
 */
export interface RiskAssessment {
  high_severity_concerns: number;
  touches_protected_paths: string[];
  modifies_dependencies: boolean;
  modifies_ci_config: boolean;
  modifies_migrations: boolean;
  step_count_exceeds_normal: boolean;
  unclear_acceptance: number;
  overall: 'low' | 'medium' | 'high';
}

export interface Task {
  id: string;
  project_id: string;
  parent_task_id: string | null;
  title: string;
  prompt: string;
  status: TaskStatus;
  pipeline_task_id: string | null;
  branch: string | null;
  cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  plan_json: unknown | null;
  force_approval: boolean;
  /** Manual-retry counter; incremented on user "Retry" clicks. */
  retry_count: number;
  approved: boolean | null;
  base_branch: string | null;
  /** Unattended mode: pipeline resolves questions + auto-approves (risk-gated). */
  autopilot: boolean;
  clarifying_questions?: ClarifyingQuestion[] | null;
  clarifying_answers?: ClarifyingAnswer[] | null;
  plan_feedback?: string | null;
  /** Pipeline's risk verdict on the plan: low / medium / high (informational). */
  risk_overall: 'low' | 'medium' | 'high' | null;
  /** Full risk assessment JSON for detail rendering. */
  risk_assessment: RiskAssessment | null;
  /**
   * Pipeline-synthesized summary of what this task accomplished.
   * Only set when status === 'completed'. Used as the parent-context
   * banner when creating sub-tasks.
   */
  task_summary: string | null;
  /** Worker-pool routing. Distinct from status (lifecycle). */
  phase: TaskPhase;
  approved_at: string | null;
  worktree_path: string | null;
  planning_worker_id: string | null;
  queued_at: string;
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  creator_name?: string | null;
  creator_email?: string | null;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  uploaded_by: string | null;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  stored_path: string;
  created_at: string;
}

export interface Comment {
  id: string;
  task_id: string;
  author_user_id: string | null;
  body: string;
  kind: 'chat' | 'approval_request' | 'approval_decision' | 'system_event';
  triggered_continuation: boolean;
  triggered_task_id: string | null;
  created_at: string;
}

export interface TaskEvent {
  id: number;
  task_id: string;
  kind: string;
  payload: unknown;
  created_at: string;
}

export interface Phase {
  id: string;
  index: number;
  name: string;
  description: string;
  acceptance_criteria: string[];
  steps: Step[];
}

export interface Step {
  id: string;
  index: number;
  description: string;
  acceptance_criteria: string[];
  files_likely_to_change: string[];
  risks?: string[];
}

export interface Plan {
  task_id: string;
  summary: string;
  phases: Phase[];
  steps: Step[];
  total_steps: number;
  total_phases: number;
}

export const STATUS_LABELS: Record<TaskStatus, string> = {
  queued: 'Backlog',
  planning: 'Planning',
  pending_approval: 'Pending Approval',
  pending_questions: 'Needs Clarification',
  pending_questions_in_execution: 'Blocking Question',
  running: 'Running',
  held: 'On Hold',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const STATUS_COLORS: Record<TaskStatus, string> = {
  queued: 'bg-slate-100 text-slate-700 border-slate-300',
  planning: 'bg-blue-50 text-blue-700 border-blue-300',
  pending_approval: 'bg-amber-50 text-amber-800 border-amber-300',
  pending_questions: 'bg-orange-50 text-orange-800 border-orange-300',
  // Distinct red so a blocking execution-pause stands out from the
  // milder orange of planning-time Comprehender questions.
  pending_questions_in_execution: 'bg-red-50 text-red-800 border-red-400',
  running: 'bg-emerald-50 text-emerald-700 border-emerald-300',
  held: 'bg-purple-50 text-purple-700 border-purple-300',
  completed: 'bg-green-50 text-green-700 border-green-300',
  failed: 'bg-red-50 text-red-700 border-red-300',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-300',
};
